import React, { useState } from "react";
import CalculatorPage from "./CalculatorPage";

// Zakat is 2.5% of net zakatable wealth held for one lunar year, provided
// that wealth meets or exceeds the nisab threshold. Nisab is the market
// value of a fixed weight of gold (87.48 g) or silver (612.36 g); most
// scholars recommend the silver basis as it is lower, so more people qualify.
const ZAKAT_RATE = 0.025;
const GOLD_NISAB_GRAMS = 87.48;
const SILVER_NISAB_GRAMS = 612.36;

// Approximate PKR price per gram, prefilled so the nisab and a result appear
// immediately. These are editable — users should update them to the current
// market rate for an exact threshold. Rough values as of mid-2026.
const MODES = [
  { id: "silver", label: "Silver nisab (recommended)", grams: SILVER_NISAB_GRAMS, defaultPrice: "420" },
  { id: "gold", label: "Gold nisab", grams: GOLD_NISAB_GRAMS, defaultPrice: "36275" },
];

const fmt = (n) =>
  Number(n).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

function ZakatCalculator() {
  const [mode, setMode] = useState("silver");
  const [pricePerGram, setPricePerGram] = useState(MODES[0].defaultPrice);
  const [cash, setCash] = useState("");
  const [metals, setMetals] = useState("");
  const [investments, setInvestments] = useState("");
  const [receivables, setReceivables] = useState("");
  const [liabilities, setLiabilities] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [funFact, setFunFact] = useState("");
  const [showFunFact, setShowFunFact] = useState(false);

  const clearOutput = () => {
    setResult(null);
    setError("");
    setShowFunFact(false);
  };

  const reset = (nextMode) => {
    setMode(nextMode);
    setPricePerGram(MODES.find((m) => m.id === nextMode).defaultPrice);
    clearOutput();
  };

  const onInput = (setter) => (e) => {
    setter(e.target.value);
    clearOutput();
  };

  const calculate = () => {
    const price = parseFloat(pricePerGram);
    if (isNaN(price) || price <= 0) {
      setError(
        "Please enter the current price per gram for the selected metal to set the nisab."
      );
      setResult(null);
      setShowFunFact(false);
      return;
    }

    const activeMode = MODES.find((m) => m.id === mode);
    const nisab = activeMode.grams * price;

    const totalAssets =
      num(cash) + num(metals) + num(investments) + num(receivables);
    const netWealth = totalAssets - num(liabilities);

    const meetsNisab = netWealth >= nisab;
    const zakatDue = meetsNisab ? netWealth * ZAKAT_RATE : 0;

    setError("");
    setResult({ nisab, netWealth, meetsNisab, zakatDue });

    const funFactsList = [
      "Zakat is 2.5% of your net qualifying wealth — one of the five pillars of Islam.",
      "Personal-use items like your home, car, and clothing are not counted for zakat.",
      "Zakat is due only after you have held wealth above the nisab for one full lunar (Hijri) year.",
      "The silver nisab is lower than the gold nisab, so using it means more of your wealth is given in charity.",
      "Debts you owe that are due now can be subtracted before calculating zakat.",
    ];
    setFunFact(funFactsList[Math.floor(Math.random() * funFactsList.length)]);
    setShowFunFact(true);
  };

  const activeMode = MODES.find((m) => m.id === mode);

  return (
    <CalculatorPage
      title="Zakat Calculator"
      metaDescription="Free Zakat calculator: add your cash, gold, silver, investments, and receivables, subtract debts, and see whether you meet the nisab and how much Zakat (2.5%) you owe."
      intro={
        <>
          Calculate your <strong>Zakat</strong> — 2.5% of your net qualifying
          wealth — and check whether your wealth meets the{" "}
          <strong>nisab</strong> threshold, using the current gold or silver
          price you provide.
        </>
      }
      seoContent={
        <>
          <h2>How Zakat is calculated</h2>
          <p>
            Zakat is 2.5% of your net zakatable wealth, payable once that
            wealth has stayed at or above the nisab for one full lunar year.
            Zakatable wealth includes cash and bank balances, the value of gold
            and silver, business stock and investments, and money owed to you
            that you expect to recover. Debts and bills that are currently due
            can be subtracted. Everyday personal items — your home, car,
            clothing and furniture — are not counted.
          </p>
          <h2>What is nisab and which threshold to use</h2>
          <p>
            Nisab is the minimum amount of wealth you must hold before Zakat
            becomes obligatory. It equals the market value of 87.48 grams of
            gold or 612.36 grams of silver. Because prices change daily, this
            calculator asks you to enter the current price per gram for the
            metal you choose, then works out the nisab for you. Most scholars
            recommend the silver basis: it produces a lower threshold, so more
            people qualify to give and more reaches those in need. This tool is
            an estimate for guidance — consult a qualified scholar for rulings
            on specific assets.
          </p>
          <h2>Frequently asked questions</h2>
          <h3>What is the rate of Zakat?</h3>
          <p>
            Zakat is 2.5% of your net zakatable wealth, due once that wealth
            has stayed at or above the nisab for one full lunar year.
          </p>
          <h3>Should I use the gold or silver nisab?</h3>
          <p>
            Most scholars recommend the silver nisab because it sets a lower
            threshold, meaning more people qualify to give and more reaches
            those in need. This calculator defaults to silver but lets you
            switch to gold.
          </p>
          <h3>Do I pay Zakat on money I owe?</h3>
          <p>
            Debts and bills that are currently due can be subtracted from your
            zakatable wealth before the 2.5% is applied. Money others owe you
            that you expect to recover is added in.
          </p>
        </>
      }
    >
      <div className="mode-tabs">
        {MODES.map((m) => (
          <button
            key={m.id}
            className={`mode-tab ${mode === m.id ? "active" : ""}`}
            onClick={() => reset(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="fields">
        <label className="field">
          <span>
            Nisab benchmark — {activeMode.id === "silver" ? "silver" : "gold"} price per gram (PKR)
          </span>
          <input
            type="number"
            value={pricePerGram}
            onChange={onInput(setPricePerGram)}
            placeholder={activeMode.id === "silver" ? "e.g. 300" : "e.g. 25000"}
          />
          <small className="field-hint">
            The nisab threshold is the market value of{" "}
            {activeMode.id === "silver" ? "612.36 g of silver" : "87.48 g of gold"}
            , so you don't need to own any{" "}
            {activeMode.id === "silver" ? "silver" : "gold"}. We've prefilled an
            approximate rate — update it to today's price for an exact
            threshold.
          </small>
        </label>
        <label className="field">
          <span>Cash &amp; bank savings (PKR)</span>
          <input type="number" value={cash} onChange={onInput(setCash)} placeholder="e.g. 500000" />
        </label>
        <label className="field">
          <span>Gold &amp; silver you own — value (PKR, optional)</span>
          <input type="number" value={metals} onChange={onInput(setMetals)} placeholder="e.g. 300000" />
        </label>
        <label className="field">
          <span>Investments &amp; business assets (PKR, optional)</span>
          <input type="number" value={investments} onChange={onInput(setInvestments)} placeholder="e.g. 200000" />
        </label>
        <label className="field">
          <span>Money owed to you (PKR, optional)</span>
          <input type="number" value={receivables} onChange={onInput(setReceivables)} placeholder="e.g. 50000" />
        </label>
        <label className="field">
          <span>Debts &amp; liabilities due (PKR, optional)</span>
          <input type="number" value={liabilities} onChange={onInput(setLiabilities)} placeholder="e.g. 100000" />
        </label>
      </div>

      <button className="btn" onClick={calculate}>Calculate Zakat</button>

      {error && <div className="error">{error}</div>}

      {result && (
        <>
          <div className="result result-breakdown">
            <div>
              Nisab threshold: <strong>Rs {fmt(result.nisab)}</strong>
            </div>
            <div>
              Net zakatable wealth: <strong>Rs {fmt(result.netWealth)}</strong>
            </div>
            {result.meetsNisab ? (
              <div>
                Zakat payable (2.5%): <strong>Rs {fmt(result.zakatDue)}</strong>
              </div>
            ) : (
              <div>
                Your net wealth is below the nisab, so{" "}
                <strong>no Zakat is due</strong> on it this year.
              </div>
            )}
          </div>
          {showFunFact && (
            <div className="fun-fact fun-fact-animate">💡 {funFact}</div>
          )}
        </>
      )}
    </CalculatorPage>
  );
}

export default ZakatCalculator;
